import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Text, Heading, Input, SortableTableHeader, LoadingSpinner, Button } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { modulesService } from '../services/modules'
import { superappService } from '../services/superapp'
import {
  SuperappUser,
  SuperappUserBehavior,
  DashboardMetrics,
  SuperappUserFilters,
  SuperappBehaviorFilters,
  SuperappPromotion,
  SuperappPromotionFilters
} from '../types/superapp'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts'

type ActiveTab = 'dashboard' | 'usuarios' | 'promocoes'

const SuperappDashboard: React.FC = () => {
  // Estados gerais
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard')
  const [moduleTitle, setModuleTitle] = useState<string>('Superapp Dashboard')
  const [moduleDescription, setModuleDescription] = useState<string>('Dashboard de análise de comportamento e gestão de usuários')

  // Estados Dashboard
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)

  // Modal de comportamentos (aberto ao clicar em usuário)
  const [selectedBehavior, setSelectedBehavior] = useState<SuperappUserBehavior | null>(null)
  const [isBehaviorModalOpen, setIsBehaviorModalOpen] = useState(false)

  // Estados Usuários
  const [usersData, setUsersData] = useState<SuperappUser[]>([])
  const [filtroNomeUsuario, setFiltroNomeUsuario] = useState('')
  const [filtroEmailUsuario, setFiltroEmailUsuario] = useState('')
  const [filtroCpfUsuario, setFiltroCpfUsuario] = useState('')
  const [filtroTelefoneUsuario, setFiltroTelefoneUsuario] = useState('')
  const [filtroRiscoChurn, setFiltroRiscoChurn] = useState<'' | 'sim' | 'nao'>('')
  const [filtroStatus, setFiltroStatus] = useState<'' | 'ativo' | 'inativo'>('')
  const [usersBehaviorStatusMap, setUsersBehaviorStatusMap] = useState<Record<string, { is_active: boolean; risk_churn: boolean }>>({})
  const [selectedUser, setSelectedUser] = useState<SuperappUser | null>(null)
  const [selectedUserBehaviorCount, setSelectedUserBehaviorCount] = useState<number>(0)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [sortByUser, setSortByUser] = useState<'user_id' | 'name' | 'email' | 'cpf' | 'phone' | 'created_at' | 'updated_at'>('created_at')
  const [sortOrderUser, setSortOrderUser] = useState<'asc' | 'desc'>('desc')
  const [currentPageUser, setCurrentPageUser] = useState(1)

  // Estados Promoções
  const [promotionsData, setPromotionsData] = useState<SuperappPromotion[]>([])
  const [filtroTitle, setFiltroTitle] = useState('')
  const [filtroEstablishmentName, setFiltroEstablishmentName] = useState('')
  const [filtroStartDate, setFiltroStartDate] = useState('')
  const [filtroEndDate, setFiltroEndDate] = useState('')
  const [filtroType, setFiltroType] = useState('')
  const [selectedPromotion, setSelectedPromotion] = useState<SuperappPromotion | null>(null)
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false)
  const [sortByPromotion, setSortByPromotion] = useState<'title' | 'establishment_name' | 'start_date' | 'end_date' | 'type'>('start_date')
  const [sortOrderPromotion, setSortOrderPromotion] = useState<'asc' | 'desc'>('desc')
  const [currentPagePromotion, setCurrentPagePromotion] = useState(1)

  // Carregar informações do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('superapp_dashboard')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Carregar métricas do dashboard
  useEffect(() => {
    const loadDashboardMetrics = async () => {
      if (activeTab !== 'dashboard') return

      try {
        setDashboardLoading(true)
        const metrics = await superappService.getDashboardMetrics()
        setDashboardMetrics(metrics)
      } catch (err: any) {
        console.error('Erro ao carregar métricas:', err)
        setError(err.message)
      } finally {
        setDashboardLoading(false)
      }
    }

    loadDashboardMetrics()
  }, [activeTab])

  // Carregar usuários e mapa de status (comportamento) para filtros
  useEffect(() => {
    const loadUsers = async () => {
      if (activeTab !== 'usuarios') return

      try {
        setLoading(true)
        const filters: SuperappUserFilters = {
          name: filtroNomeUsuario || undefined,
          email: filtroEmailUsuario || undefined,
          cpf: filtroCpfUsuario || undefined,
          phone: filtroTelefoneUsuario || undefined
        }
        const [users, statusMap] = await Promise.all([
          superappService.getAllUsers(filters),
          superappService.getUsersBehaviorStatusMap()
        ])
        setUsersData(users)
        setUsersBehaviorStatusMap(statusMap)
      } catch (err: any) {
        console.error('Erro ao carregar usuários:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadUsers()
  }, [activeTab, filtroNomeUsuario, filtroEmailUsuario, filtroCpfUsuario, filtroTelefoneUsuario])

  // Carregar promoções
  useEffect(() => {
    const loadPromotions = async () => {
      if (activeTab !== 'promocoes') return

      try {
        setLoading(true)
        const filters: SuperappPromotionFilters = {
          title: filtroTitle || undefined,
          establishment_name: filtroEstablishmentName || undefined,
          start_date: filtroStartDate || undefined,
          end_date: filtroEndDate || undefined,
          type: filtroType || undefined
        }
        const promotions = await superappService.getPromotions(filters)
        setPromotionsData(promotions)
      } catch (err: any) {
        console.error('Erro ao carregar promoções:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadPromotions()
  }, [activeTab, filtroTitle, filtroEstablishmentName, filtroStartDate, filtroEndDate, filtroType])

  // Carregar contagem de comportamentos do usuário selecionado
  useEffect(() => {
    const loadUserBehaviorCount = async () => {
      if (!selectedUser) return

      try {
        const count = await superappService.getUserBehaviorCount(selectedUser.user_id)
        setSelectedUserBehaviorCount(count)
      } catch (err) {
        console.error('Erro ao carregar contagem de comportamentos:', err)
        setSelectedUserBehaviorCount(0)
      }
    }

    if (isUserModalOpen && selectedUser) {
      loadUserBehaviorCount()
    }
  }, [isUserModalOpen, selectedUser])

  // Filtrar e ordenar usuários
  const usersFiltrados = useMemo(() => {
    let filtered = [...usersData]

    // Filtro por status (ativo/inativo) e risco de churn
    if (filtroStatus || filtroRiscoChurn) {
      filtered = filtered.filter((user) => {
        const status = usersBehaviorStatusMap[user.user_id] ?? { is_active: false, risk_churn: false }
        if (filtroStatus === 'ativo' && !status.is_active) return false
        if (filtroStatus === 'inativo' && status.is_active) return false
        if (filtroRiscoChurn === 'sim' && !status.risk_churn) return false
        if (filtroRiscoChurn === 'nao' && status.risk_churn) return false
        return true
      })
    }

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue: any = a[sortByUser]
      let bValue: any = b[sortByUser]

      if (sortByUser === 'created_at' || sortByUser === 'updated_at') {
        // Tratar valores nulos: null/undefined vão para o final
        if (!aValue && !bValue) return 0
        if (!aValue) return 1
        if (!bValue) return -1
        const aDate = new Date(aValue).getTime()
        const bDate = new Date(bValue).getTime()
        aValue = isNaN(aDate) ? 0 : aDate
        bValue = isNaN(bDate) ? 0 : bDate
      } else {
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (sortOrderUser === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    return filtered
  }, [usersData, sortByUser, sortOrderUser, filtroStatus, filtroRiscoChurn, usersBehaviorStatusMap])

  // Filtrar e ordenar promoções
  const promotionsFiltradas = useMemo(() => {
    let filtered = [...promotionsData]

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue: any = a[sortByPromotion]
      let bValue: any = b[sortByPromotion]

      if (sortByPromotion === 'start_date' || sortByPromotion === 'end_date') {
        // Tratar valores nulos: null/undefined vão para o final
        if (!aValue && !bValue) return 0
        if (!aValue) return 1
        if (!bValue) return -1
        const aDate = new Date(aValue).getTime()
        const bDate = new Date(bValue).getTime()
        aValue = isNaN(aDate) ? 0 : aDate
        bValue = isNaN(bDate) ? 0 : bDate
      } else {
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (sortOrderPromotion === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    return filtered
  }, [promotionsData, sortByPromotion, sortOrderPromotion])

  // Paginação
  const itemsPerPage = 20
  const totalPagesUser = Math.ceil(usersFiltrados.length / itemsPerPage)
  const startIndexUser = (currentPageUser - 1) * itemsPerPage
  const endIndexUser = startIndexUser + itemsPerPage
  const usersPaginados = usersFiltrados.slice(startIndexUser, endIndexUser)

  const totalPagesPromotion = Math.ceil(promotionsFiltradas.length / itemsPerPage)
  const startIndexPromotion = (currentPagePromotion - 1) * itemsPerPage
  const endIndexPromotion = startIndexPromotion + itemsPerPage
  const promotionsPaginadas = promotionsFiltradas.slice(startIndexPromotion, endIndexPromotion)

  // Handlers de paginação
  const handlePageChangeUser = (page: number) => {
    setCurrentPageUser(page)
  }

  const handlePreviousPageUser = () => {
    if (currentPageUser > 1) {
      setCurrentPageUser(currentPageUser - 1)
    }
  }

  const handleNextPageUser = () => {
    if (currentPageUser < totalPagesUser) {
      setCurrentPageUser(currentPageUser + 1)
    }
  }

  const handlePageChangePromotion = (page: number) => {
    setCurrentPagePromotion(page)
  }

  const handlePreviousPagePromotion = () => {
    if (currentPagePromotion > 1) {
      setCurrentPagePromotion(currentPagePromotion - 1)
    }
  }

  const handleNextPagePromotion = () => {
    if (currentPagePromotion < totalPagesPromotion) {
      setCurrentPagePromotion(currentPagePromotion + 1)
    }
  }

  // Navegação horizontal (tabelas)
  const usersScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollUsersBy = (dx: number) => {
    usersScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handleUsersWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      usersScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  const promotionsScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollPromotionsBy = (dx: number) => {
    promotionsScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handlePromotionsWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      promotionsScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPageUser(1)
  }, [filtroNomeUsuario, filtroEmailUsuario, filtroCpfUsuario, filtroTelefoneUsuario, filtroStatus, filtroRiscoChurn])

  useEffect(() => {
    setCurrentPagePromotion(1)
  }, [filtroTitle, filtroEstablishmentName, filtroStartDate, filtroEndDate, filtroType])

  // Handlers
  const toggleSortUser = (key: string) => {
    if (sortByUser === key) {
      setSortOrderUser(sortOrderUser === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByUser(key as any)
      setSortOrderUser('asc')
    }
  }

  const toggleSortPromotion = (key: string) => {
    if (sortByPromotion === key) {
      setSortOrderPromotion(sortOrderPromotion === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByPromotion(key as any)
      setSortOrderPromotion('asc')
    }
  }

  const handleCloseBehaviorModal = () => {
    setIsBehaviorModalOpen(false)
    setSelectedBehavior(null)
  }

  const handleUserRowClick = async (item: SuperappUser) => {
    setSelectedUser(item)

    let behaviorForUser: SuperappUserBehavior | null = null
    try {
      const filters: SuperappBehaviorFilters = {}
      if (item.email) filters.email = item.email
      else if (item.name) filters.name = item.name
      if (filters.email || filters.name) {
        const behaviors = await superappService.getUserBehaviors(filters)
        behaviorForUser = behaviors[0] || null
      }
    } catch (err) {
      console.error('Erro ao carregar comportamento do usuário:', err)
    }

    if (!behaviorForUser) {
      behaviorForUser = {
        tenant_id: '',
        behavior_data: null,
        created_at: item.created_at || new Date().toISOString(),
        user_id: item.user_id,
        user: item
      }
    }

    setSelectedBehavior(behaviorForUser)
    setIsBehaviorModalOpen(true)
  }

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setSelectedUser(null)
    setSelectedUserBehaviorCount(0)
  }

  const handlePromotionRowClick = (item: SuperappPromotion) => {
    setSelectedPromotion(item)
    setIsPromotionModalOpen(true)
  }

  const handleClosePromotionModal = () => {
    setIsPromotionModalOpen(false)
    setSelectedPromotion(null)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Sao_Paulo'
    })
  }

  const formatDateShort = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    
    // Se a string está no formato YYYY-MM-DD, interpretar como data local em São Paulo
    // para evitar problemas de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      // Criar data no timezone de São Paulo
      const dateSP = new Date(year, month - 1, day)
      return dateSP.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
    
    // Para outros formatos, usar o método padrão
    const date = new Date(dateString)
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    })
  }

  const formatCpf = (cpf: string | null | undefined): string => {
    if (!cpf) return '-'
    // Remove caracteres não numéricos
    const cleaned = cpf.replace(/\D/g, '')
    // Aplica a máscara XXX.XXX.XXX-XX
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
    // Se não tiver 11 dígitos, retorna sem formatação
    return cpf
  }

  const formatJson = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      return JSON.stringify(data, null, 2)
    } catch (error) {
      return String(data)
    }
  }

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '-'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const isPromotionActive = (startDate: string, endDate: string): boolean => {
    const now = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)
    return now >= start && now <= end
  }

  const getUserNameFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o nome do relacionamento com superapp_users
    if (behavior.user?.name) {
      return behavior.user.name
    }
    // Se não tiver no relacionamento, busca no JSON behavior_data
    if (behavior.behavior_data && behavior.behavior_data.length > 0) {
      return behavior.behavior_data[0]?.name || '-'
    }
    return '-'
  }

  const getUserEmailFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o email do relacionamento com superapp_users
    if (behavior.user?.email) {
      return behavior.user.email
    }
    // Se não tiver no relacionamento, busca no JSON behavior_data
    if (behavior.behavior_data && behavior.behavior_data.length > 0) {
      return behavior.behavior_data[0]?.email || '-'
    }
    return '-'
  }

  const getUserPhoneFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o telefone do relacionamento com superapp_users
    if (behavior.user?.phone) {
      return behavior.user.phone
    }
    // Se não tiver no relacionamento, busca no JSON behavior_data
    if (behavior.behavior_data && behavior.behavior_data.length > 0) {
      return behavior.behavior_data[0]?.phone || '-'
    }
    return '-'
  }

  // Dados para gráficos
  const pieChartData = useMemo(() => {
    if (!dashboardMetrics) return []
    const activeUsers = dashboardMetrics.activeUsers
    const totalUsers = dashboardMetrics.totalUsers
    const inactiveUsers = totalUsers - activeUsers
    
    return [
      { name: 'Ativo', value: activeUsers },
      { name: 'Inativo', value: inactiveUsers }
    ].filter(item => item.value > 0)
  }, [dashboardMetrics])

  const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b']

  if (error && !loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" color="muted">{error}</Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard
            moduleName="superapp_dashboard"
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleTitle}
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  {moduleDescription}
                </Text>
              </div>

              {/* Abas */}
              <div className="px-6">
                <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'dashboard' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setActiveTab('usuarios')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'usuarios' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Usuários
                  </button>
                  <button
                    onClick={() => setActiveTab('promocoes')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'promocoes' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Promoções
                  </button>
                </div>
              </div>

              {/* Conteúdo por Aba */}
              {activeTab === 'dashboard' && (
                <div className="flex-1 px-6 py-4">
                  {dashboardLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : dashboardMetrics ? (
                    <>
                      {/* Métricas */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.totalUsers}
                            </Text>
                            <Text size="sm" className="text-white">
                              Total de Usuários
                            </Text>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.activeUsers}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Usuários Ativos
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80 text-left">
                                <div className="whitespace-normal">
                                  <div className="text-gray-300">
                                    <div className="mb-1">Um usuário é considerado ativo quando a última atividade ocorreu nos últimos 30 dias.</div>
                                    <div>Usuários sem atividade por mais de 30 dias são automaticamente classificados como inativos.</div>
                                  </div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.atRiskUsers}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Em Risco de Churn
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80 text-left">
                                <div className="whitespace-normal">
                                  <div className="font-semibold mb-2">Usuários identificados com risco de cancelamento ou abandono do SuperApp</div>
                                  <div className="text-gray-300 mt-2">
                                    <div className="font-medium mb-1">Um usuário é classificado como em risco de churn quando ambas as condições abaixo são atendidas:</div>
                                    <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                                      <li>Última atividade realizada há mais de 14 dias</li>
                                      <li>Score de engajamento inferior a 30 pontos</li>
                                    </ul>
                                  </div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.avgEngagementScore.toFixed(2)}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Média do Score
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute top-full right-0 mt-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-[36rem] sm:w-[40rem] text-left">
                                <div className="whitespace-normal">
                                  <div className="font-semibold mb-3 text-base">Score de Engajamento (0 a 100 pontos)</div>
                                  <div className="text-gray-300 mt-2">
                                    <div className="mb-4">O score de engajamento é calculado automaticamente com base em quatro pilares:</div>
                                    <div className="space-y-4">
                                      <div>
                                        <div className="font-medium mb-2">1. Frequência de Eventos (até 30 pontos)</div>
                                        <div className="ml-2 mb-1">Considera o total de eventos registrados pelo usuário.</div>
                                        <div className="ml-2 mb-1">• Cálculo: total de eventos dividido por 10, limitado a 30 pontos.</div>
                                        <div className="ml-2">• Exemplo: 300 eventos = 30 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">2. Variedade de Ações (até 25 pontos)</div>
                                        <div className="ml-2 mb-1">Avalia a quantidade de tipos diferentes de ações realizadas nos últimos 30 dias.</div>
                                        <div className="ml-2 mb-1">• Cálculo: número de ações únicas multiplicado por 5, limitado a 25 pontos.</div>
                                        <div className="ml-2">• Exemplo: 5 tipos diferentes de ações = 25 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">3. Consistência de Uso (até 25 pontos)</div>
                                        <div className="ml-2 mb-1">Analisa a quantidade de dias distintos com atividade nos últimos 30 dias.</div>
                                        <div className="ml-2 mb-1">• Cálculo: número de dias únicos com atividade multiplicado por 2, limitado a 25 pontos.</div>
                                        <div className="ml-2">• Exemplo: 13 dias ou mais com atividade = 25 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">4. Recência da Última Atividade (até 20 pontos)</div>
                                        <div className="ml-2 mb-1">Baseada no intervalo desde a última atividade do usuário:</div>
                                        <div className="ml-2 mb-1">• Atividade realizada hoje: 20 pontos</div>
                                        <div className="ml-2 mb-1">• Entre 1 e 7 dias: 15 pontos</div>
                                        <div className="ml-2 mb-1">• Entre 8 e 14 dias: 10 pontos</div>
                                        <div className="ml-2">• Mais de 14 dias: 0 pontos</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="absolute bottom-full right-4 -mb-1">
                                  <div className="border-4 border-transparent border-b-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Gráficos */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {/* Gráfico de Barras - Usuários Cadastrados por Data */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Usuários Cadastrados por Data (últimos 3 meses)
                          </Heading>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={dashboardMetrics.behaviorsByDate}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis
                                  dataKey="date"
                                  stroke="#6b7280"
                                  fontSize={12}
                                  tickFormatter={formatDateShort}
                                />
                                <YAxis
                                  stroke="#6b7280"
                                  fontSize={12}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#f9fafb'
                                  }}
                                  formatter={(value: any) => [`${value} usuário(s)`, 'Usuários Cadastrados']}
                                  labelFormatter={(label: string) => `Data: ${formatDateShort(label)}`}
                                />
                                <Bar
                                  dataKey="count"
                                  radius={[4, 4, 0, 0]}
                                  fill="#3b82f6"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Gráfico de Pizza - Distribuição por Status */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Distribuição de Usuários por Status
                          </Heading>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieChartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {pieChartData.map((entry, index) => {
                                    const color = entry.name === 'Inativo' ? '#ef4444' : entry.name === 'Ativo' ? '#10b981' : COLORS[index % COLORS.length]
                                    return <Cell key={`cell-${index}`} fill={color} />
                                  })}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: '#1f2937',
                                      border: 'none',
                                      borderRadius: '8px',
                                      color: '#f9fafb'
                                    }}
                                  />
                                  <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Gráfico de Barras - Módulo Favorito */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Módulo Favorito
                          </Heading>
                          <div className="h-64">
                            {dashboardMetrics.favoriteModules.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={dashboardMetrics.favoriteModules.map(item => ({
                                    name: item.module,
                                    value: item.count
                                  }))}
                                  layout="vertical"
                                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                  <XAxis type="number" className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <YAxis type="category" dataKey="name" width={75} className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: '#1f2937',
                                      border: 'none',
                                      borderRadius: '8px',
                                      color: '#f9fafb'
                                    }}
                                    formatter={(value: number) => [value, 'Usuários']}
                                    labelFormatter={(label) => `Módulo: ${label}`}
                                  />
                                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                                    {dashboardMetrics.favoriteModules.map((_, index) => (
                                      <Cell key={`module-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Gráfico de Pizza - Horário Preferido */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Horário Preferido
                          </Heading>
                          <div className="h-64">
                            {dashboardMetrics.preferredTimeSlots.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={dashboardMetrics.preferredTimeSlots.map(item => ({
                                      name: item.timeSlot,
                                      value: item.count
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {dashboardMetrics.preferredTimeSlots.map((entry, index) => (
                                      <Cell key={`timeslot-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: '#1f2937',
                                      border: 'none',
                                      borderRadius: '8px',
                                      color: '#f9fafb'
                                    }}
                                  />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <Text size="lg" color="muted">Nenhum dado disponível</Text>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'usuarios' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Filtros */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                        Filtros
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Nome
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o nome..."
                            value={filtroNomeUsuario}
                            onChange={(e) => setFiltroNomeUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Email
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o email..."
                            value={filtroEmailUsuario}
                            onChange={(e) => setFiltroEmailUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            CPF
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o CPF..."
                            value={filtroCpfUsuario}
                            onChange={(e) => setFiltroCpfUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Telefone
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o telefone..."
                            value={filtroTelefoneUsuario}
                            onChange={(e) => setFiltroTelefoneUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Status
                          </Text>
                          <select
                            value={filtroStatus}
                            onChange={(e) => setFiltroStatus((e.target.value || '') as '' | 'ativo' | 'inativo')}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Risco de churn
                          </Text>
                          <select
                            value={filtroRiscoChurn}
                            onChange={(e) => setFiltroRiscoChurn((e.target.value || '') as '' | 'sim' | 'nao')}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFiltroNomeUsuario('')
                              setFiltroEmailUsuario('')
                              setFiltroCpfUsuario('')
                              setFiltroTelefoneUsuario('')
                              setFiltroStatus('')
                              setFiltroRiscoChurn('')
                            }}
                          >
                            Limpar Filtros
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Tabela */}
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => scrollUsersBy(-320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a esquerda"
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollUsersBy(320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a direita"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                          <div ref={usersScrollRef} className="overflow-x-auto" onWheel={handleUsersWheel}>
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <SortableTableHeader
                                    sortKey="name"
                                    currentSort={sortByUser}
                                    sortOrder={sortOrderUser}
                                    onSort={toggleSortUser}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300 w-48 sticky left-0 z-20 bg-gray-50 dark:bg-gray-700"
                                  >
                                    Nome
                                  </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="email"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Email
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="cpf"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  CPF
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="phone"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Telefone
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="created_at"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Data de Criação
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="updated_at"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Data de Atualização
                                </SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {usersPaginados.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                    Nenhum usuário encontrado
                                  </td>
                                </tr>
                              ) : (
                                usersPaginados.map((user) => (
                                  <tr
                                    key={user.user_id}
                                    onClick={() => handleUserRowClick(user)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 w-48 sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 group-hover:bg-gray-100 dark:group-hover:bg-gray-600">
                                      {user.name || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {user.email || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatCpf(user.cpf)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {user.phone || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatDate(user.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatDate(user.updated_at)}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Paginação */}
                        {totalPagesUser > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexUser + 1} a {Math.min(endIndexUser, usersFiltrados.length)} de {usersFiltrados.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageUser}
                                  disabled={currentPageUser === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesUser) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesUser <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageUser <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageUser >= totalPagesUser - 2) {
                                      pageNumber = totalPagesUser - 4 + i
                                    } else {
                                      pageNumber = currentPageUser - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeUser(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageUser === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageUser}
                                  disabled={currentPageUser === totalPagesUser}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'promocoes' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Filtros */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                        Filtros
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Título
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o título..."
                            value={filtroTitle}
                            onChange={(e) => setFiltroTitle(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Estabelecimento
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o nome..."
                            value={filtroEstablishmentName}
                            onChange={(e) => setFiltroEstablishmentName(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Data Início
                          </Text>
                          <Input
                            type="date"
                            value={filtroStartDate}
                            onChange={(e) => setFiltroStartDate(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Data Fim
                          </Text>
                          <Input
                            type="date"
                            value={filtroEndDate}
                            onChange={(e) => setFiltroEndDate(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Tipo
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o tipo..."
                            value={filtroType}
                            onChange={(e) => setFiltroType(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFiltroTitle('')
                              setFiltroEstablishmentName('')
                              setFiltroStartDate('')
                              setFiltroEndDate('')
                              setFiltroType('')
                            }}
                          >
                            Limpar Filtros
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Tabela */}
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => scrollPromotionsBy(-320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a esquerda"
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollPromotionsBy(320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a direita"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                          <div ref={promotionsScrollRef} className="overflow-x-auto" onWheel={handlePromotionsWheel}>
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <SortableTableHeader
                                    sortKey="title"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Título
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="establishment_name"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Estabelecimento
                                  </SortableTableHeader>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                    Status
                                  </th>
                                  <SortableTableHeader
                                    sortKey="start_date"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Data Início
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="end_date"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Data Fim
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="type"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Tipo
                                  </SortableTableHeader>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                    Código de Cópia
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {promotionsPaginadas.length === 0 ? (
                                  <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                      Nenhuma promoção encontrada
                                    </td>
                                  </tr>
                                ) : (
                                  promotionsPaginadas.map((promotion) => (
                                    <tr
                                      key={promotion.id}
                                      onClick={() => handlePromotionRowClick(promotion)}
                                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    >
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {truncateText(promotion.title, 30)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.establishment_name}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {isPromotionActive(promotion.start_date, promotion.end_date) ? (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                            Ativa
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                            Inativa
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {formatDate(promotion.start_date)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {formatDate(promotion.end_date)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.type}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.copy_code || '-'}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Paginação */}
                        {totalPagesPromotion > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexPromotion + 1} a {Math.min(endIndexPromotion, promotionsFiltradas.length)} de {promotionsFiltradas.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPagePromotion}
                                  disabled={currentPagePromotion === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesPromotion) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesPromotion <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPagePromotion <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPagePromotion >= totalPagesPromotion - 2) {
                                      pageNumber = totalPagesPromotion - 4 + i
                                    } else {
                                      pageNumber = currentPagePromotion - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangePromotion(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPagePromotion === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPagePromotion}
                                  disabled={currentPagePromotion === totalPagesPromotion}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Modal de Detalhes do Comportamento */}
              {isBehaviorModalOpen && selectedBehavior && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseBehaviorModal}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Detalhes do Comportamento
                        </Heading>
                        <button onClick={handleCloseBehaviorModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        {selectedBehavior.behavior_data && selectedBehavior.behavior_data.length > 0 ? (
                          <div className="space-y-6">
                            {selectedBehavior.behavior_data.map((user, index) => (
                              <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-b-0 last:pb-0">
                                <div className="grid grid-cols-2 gap-4">
                                  {/* Informações Básicas */}
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      CPF
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {formatCpf(user.cpf)}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Nome
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.name || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Email
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.email || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Telefone
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.phone || '-'}
                                    </Text>
                                  </div>

                                  {/* Atividade */}
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Última Visualização
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.atividade?.last_seen_at ? formatDate(user.atividade.last_seen_at) : '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Primeira Visualização
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.atividade?.first_seen_at ? formatDate(user.atividade.first_seen_at) : '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Dias de Vida do Cliente
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.atividade?.customer_lifetime_days ?? '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Dias Desde Última Atividade
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.atividade?.days_since_last_activity ?? '-'}
                                    </Text>
                                  </div>

                                  {/* Dispositivo */}
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Sistema Operacional
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.dispositivo?.device_os || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Versão do App
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.dispositivo?.app_version || '-'}
                                    </Text>
                                  </div>

                                  {/* Localização */}
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Cidade
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.localizacao?.city || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Latitude
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.localizacao?.latitude ?? '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Longitude
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.localizacao?.longitude ?? '-'}
                                    </Text>
                                  </div>

                                  {/* Comportamento */}
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Status Ativo
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.is_active ? 'Sim' : 'Não'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Risco de Churn
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.risk_churn ? 'Sim' : 'Não'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Última Ação
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.last_action || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Total de Eventos
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.total_events ?? '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Módulo Favorito
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.favorite_module || '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Score de Engajamento
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.engagement_score ?? '-'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                                      Horário Preferido
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {user.comportamento?.preferred_time_slot || '-'}
                                    </Text>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">
                              Nenhum dado de comportamento disponível
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de Detalhes do Usuário */}
              {isUserModalOpen && selectedUser && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseUserModal}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Detalhes do Usuário
                        </Heading>
                        <button onClick={handleCloseUserModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                ID do Usuário
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedUser.user_id}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Nome
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedUser.name || '-'}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Email
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedUser.email || '-'}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                CPF
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatCpf(selectedUser.cpf)}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Telefone
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedUser.phone || '-'}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Criação
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedUser.created_at)}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Atualização
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedUser.updated_at)}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de Detalhes da Promoção */}
              {isPromotionModalOpen && selectedPromotion && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClosePromotionModal}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Detalhes da Promoção
                        </Heading>
                        <button onClick={handleClosePromotionModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        <div className="space-y-4">
                          {/* Imagem do Estabelecimento */}
                          {selectedPromotion.establishment_image && (
                            <div className="mb-4">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Imagem do Estabelecimento
                              </Text>
                              <div className="flex justify-center">
                                <img
                                  src={selectedPromotion.establishment_image}
                                  alt={selectedPromotion.establishment_name}
                                  className="max-w-full h-auto max-h-96 rounded-lg shadow-lg"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                ID
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.id}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Título
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.title}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                ID do Estabelecimento
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.establishment_id}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Nome do Estabelecimento
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.establishment_name}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                URL
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white break-all">
                                <a
                                  href={selectedPromotion.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                                >
                                  {selectedPromotion.url}
                                </a>
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Tipo
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.type}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Início
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedPromotion.start_date)}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Fim
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedPromotion.end_date)}
                              </Text>
                            </div>
                            <div className="col-span-2">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Código de Cópia
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.copy_code || '-'}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
        </main>
      </div>
    </div>
  )
}

export default SuperappDashboard
